import React, { useContext, useState } from 'react';
import { PBXCallState } from 'app/store/contexts';

const styles = {
  container: {
    flex: 1,
    display: 'flex',
    flexDirection: 'column',
    position: 'relative',
    zIndex: 1,
    padding: '20px 16px 10px',
  },
  topSpace: {
    height: '80px',
    flexShrink: 0,
  },
  avatar: {
    width: '110px',
    height: '110px',
    borderRadius: '50%',
    background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
    margin: '0 auto 20px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontSize: '48px',
    color: '#fff',
    fontWeight: 500,
    fontFamily: '-apple-system, BlinkMacSystemFont, sans-serif',
    boxShadow: '0 8px 24px rgba(102, 126, 234, 0.35)',
  },
  contactInfo: {
    textAlign: 'center',
    marginBottom: '30px',
  },
  name: {
    fontSize: '32px',
    fontWeight: 400,
    color: '#fff',
    fontFamily: '-apple-system, BlinkMacSystemFont, sans-serif',
    letterSpacing: '-0.5px',
    marginBottom: '6px',
    lineHeight: 1.1,
    wordBreak: 'break-word',
    padding: '0 8px',
  },
  number: {
    fontSize: '15px',
    fontWeight: 400,
    color: 'rgba(255,255,255,0.7)',
    fontFamily: '-apple-system, BlinkMacSystemFont, sans-serif',
    marginBottom: '6px',
  },
  status: {
    fontSize: '15px',
    fontWeight: 400,
    color: 'rgba(255,255,255,0.75)',
    fontFamily: '-apple-system, BlinkMacSystemFont, sans-serif',
    letterSpacing: '0.2px',
    marginTop: '4px',
  },
  timer: {
    fontSize: '17px',
    fontWeight: 400,
    color: 'rgba(255,255,255,0.8)',
    fontFamily: '-apple-system, "SF Mono", Menlo, monospace',
    letterSpacing: '1px',
  },
  dot: {
    width: '4px',
    height: '4px',
    borderRadius: '50%',
    background: 'rgba(255,255,255,0.75)',
  },
  spacer: {
    flex: 1,
  },
  activeCallRow: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'flex-start',
    gap: '40px',
    marginBottom: '30px',
  },
  btnCol: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    gap: '10px',
  },
  roundBtn: {
    width: '66px',
    height: '66px',
    borderRadius: '50%',
    border: 'none',
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    userSelect: 'none',
    WebkitTapHighlightColor: 'transparent',
    touchAction: 'manipulation',
    transition: 'transform 0.1s ease, background 0.2s ease',
  },
  muteBtn: {
    background: 'rgba(255, 255, 255, 0.2)',
    backdropFilter: 'blur(10px)',
    WebkitBackdropFilter: 'blur(10px)',
  },
  muteBtnActive: {
    background: '#fff',
  },
  endCallBtn: {
    width: '72px',
    height: '72px',
    borderRadius: '50%',
    background: '#ff3b30',
    border: 'none',
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    userSelect: 'none',
    WebkitTapHighlightColor: 'transparent',
    touchAction: 'manipulation',
    boxShadow: '0 4px 16px rgba(255, 59, 48, 0.5)',
    transition: 'transform 0.1s ease',
  },
  endCallWrap: {
    display: 'flex',
    justifyContent: 'center',
    marginBottom: '30px',
  },
  btnLabel: {
    fontSize: '12px',
    color: '#fff',
    fontFamily: '-apple-system, BlinkMacSystemFont, sans-serif',
    fontWeight: 500,
    textAlign: 'center',
  },
  incomingRow: {
    display: 'flex',
    justifyContent: 'space-around',
    alignItems: 'flex-start',
    padding: '0 10px',
    marginBottom: '30px',
  },
  acceptBtn: {
    background: '#34c759',
    boxShadow: '0 4px 14px rgba(52, 199, 89, 0.45)',
    animation: 'acceptPulse 1.6s ease-in-out infinite',
  },
  declineBtn: {
    background: '#ff3b30',
    boxShadow: '0 4px 14px rgba(255, 59, 48, 0.45)',
  },
  callingDotsStacked: {
    display: 'flex',
    justifyContent: 'center',
    gap: '6px',
    marginTop: '8px',
  },
  updateInfoBtn: {
    background: 'rgba(255,255,255,0.15)',
    backdropFilter: 'blur(10px)',
    WebkitBackdropFilter: 'blur(10px)',
    border: '1px solid rgba(255,255,255,0.2)',
    borderRadius: '18px',
    padding: '8px 20px',
    color: '#fff',
    fontSize: '13px',
    fontWeight: 600,
    fontFamily: '-apple-system, BlinkMacSystemFont, sans-serif',
    cursor: 'pointer',
    marginTop: '12px',
    userSelect: 'none',
    WebkitTapHighlightColor: 'transparent',
    transition: 'background 0.2s ease',
  },
};

function formatDuration(seconds) {
  const m = Math.floor(seconds / 60);
  const s = seconds % 60;
  if (m >= 60) {
    const h = Math.floor(m / 60);
    const mm = m % 60;
    return `${h}:${mm.toString().padStart(2, '0')}:${s.toString().padStart(2, '0')}`;
  }
  return `${m.toString().padStart(2, '0')}:${s.toString().padStart(2, '0')}`;
}

function getInitials(name, fallback) {
  if (name) {
    const parts = name.trim().split(/\s+/);
    if (parts.length >= 2) return (parts[0][0] + parts[1][0]).toUpperCase();
    return name.slice(0, 2).toUpperCase();
  }
  if (fallback) return fallback.slice(-2);
  return '';
}

function PBXOutboundCallScreen({ onUpdateInfoClick, onCreateTicketClick }) {
  const {
    callState,
    isAnswering,
    remoteNumber,
    customerInfo,
    callDuration,
    isMuted,
    toggleMute,
    terminateCall,
    answerCall,
    rejectCall,
  } = useContext(PBXCallState);

  const displayName = customerInfo?.name || remoteNumber || 'Unknown';
  const displayNumber = customerInfo?.name ? remoteNumber : null;
  const initials = getInitials(customerInfo?.name, remoteNumber);

  const isActive = callState === 'active';
  const isCalling = callState === 'calling';
  const isIncoming = callState === 'incoming';
  const isConnecting = isIncoming && isAnswering;

  return (
    <>
      <style>{`
        @keyframes acceptPulse {
          0%, 100% { box-shadow: 0 4px 14px rgba(52,199,89,0.45); }
          50% { box-shadow: 0 4px 22px rgba(52,199,89,0.75); }
        }
        @keyframes dotBounce {
          0%, 80%, 100% { opacity: 0.3; transform: translateY(0); }
          40% { opacity: 1; transform: translateY(-2px); }
        }
        .pbx-dot-1 { animation: dotBounce 1.2s ease-in-out infinite; }
        .pbx-dot-2 { animation: dotBounce 1.2s ease-in-out 0.15s infinite; }
        .pbx-dot-3 { animation: dotBounce 1.2s ease-in-out 0.3s infinite; }
        @keyframes pbxSpin { to { transform: rotate(360deg); } }
        .pbx-spinner { animation: pbxSpin 0.8s linear infinite; }
      `}</style>

      <div style={styles.container}>
        <div style={styles.topSpace} />

        <div style={styles.avatar}>
          {initials || (
            <svg width="56" height="56" viewBox="0 0 24 24" fill="#fff">
              <path d="M12 12c2.21 0 4-1.79 4-4s-1.79-4-4-4-4 1.79-4 4 1.79 4 4 4zm0 2c-2.67 0-8 1.34-8 4v2h16v-2c0-2.66-5.33-4-8-4z" />
            </svg>
          )}
        </div>

        <div style={styles.contactInfo}>
          <div style={styles.name}>{displayName}</div>
          {displayNumber && <div style={styles.number}>{displayNumber}</div>}

          {isActive && (
            <div style={styles.timer}>{formatDuration(callDuration)}</div>
          )}

          {isCalling && (
            <>
              <div style={styles.status}>Outgoing</div>
              <div style={styles.status}>calling</div>
              <div style={styles.callingDotsStacked}>
                <span className="pbx-dot-1" style={styles.dot} />
                <span className="pbx-dot-2" style={styles.dot} />
                <span className="pbx-dot-3" style={styles.dot} />
              </div>
            </>
          )}

          {isIncoming && (
            <div style={styles.status}>{isConnecting ? 'Connecting...' : 'Incoming call...'}</div>
          )}
          {isActive && customerInfo?.customerId && (
            <div style={{ display: 'flex', justifyContent: 'center', gap: '10px', marginTop: '12px' }}>
              <button
                type="button"
                style={styles.updateInfoBtn}
                onPointerDown={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  onUpdateInfoClick?.();
                }}
              >
                Update Info
              </button>
              <button
                type="button"
                style={styles.updateInfoBtn}
                onPointerDown={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  onCreateTicketClick?.();
                }}
              >
                Create Ticket
              </button>
            </div>
          )}
        </div>

        <div style={styles.spacer} />

        {isIncoming && (
          <div style={styles.incomingRow}>
            <div style={styles.btnCol}>
              <button
                type="button"
                style={{ ...styles.roundBtn, ...styles.declineBtn }}
                onPointerDown={(e) => { e.preventDefault(); e.stopPropagation(); rejectCall(); }}
                title="Decline"
              >
                <svg width="28" height="28" viewBox="0 0 24 24" fill="#fff" style={{ pointerEvents: 'none', transform: 'rotate(135deg)' }}>
                  <path d="M20.01 15.38c-1.23 0-2.42-.2-3.53-.56-.35-.12-.74-.03-1.01.24l-1.57 1.97c-2.83-1.35-5.48-3.9-6.89-6.83l1.95-1.66c.27-.28.35-.67.24-1.02-.37-1.11-.56-2.3-.56-3.53 0-.54-.45-.99-.99-.99H4.19C3.65 3 3 3.24 3 3.99 3 13.28 10.73 21 20.01 21c.71 0 .99-.63.99-1.18v-3.45c0-.54-.45-.99-.99-.99z" />
                </svg>
              </button>
              <span style={styles.btnLabel}>Decline</span>
            </div>

            <div style={styles.btnCol}>
              <button
                type="button"
                disabled={isConnecting}
                style={{
                  ...styles.roundBtn,
                  ...styles.acceptBtn,
                  ...(isConnecting ? { animation: 'none', opacity: 0.7, cursor: 'default' } : null),
                }}
                onPointerDown={(e) => { e.preventDefault(); e.stopPropagation(); if (!isConnecting) answerCall(); }}
                title={isConnecting ? 'Connecting...' : 'Accept'}
              >
                {isConnecting ? (
                  <svg className="pbx-spinner" width="26" height="26" viewBox="0 0 24 24" fill="none" style={{ pointerEvents: 'none' }}>
                    <circle cx="12" cy="12" r="9" stroke="rgba(255,255,255,0.35)" strokeWidth="3" />
                    <path d="M21 12a9 9 0 0 0-9-9" stroke="#fff" strokeWidth="3" strokeLinecap="round" />
                  </svg>
                ) : (
                  <svg width="28" height="28" viewBox="0 0 24 24" fill="#fff" style={{ pointerEvents: 'none' }}>
                    <path d="M20.01 15.38c-1.23 0-2.42-.2-3.53-.56-.35-.12-.74-.03-1.01.24l-1.57 1.97c-2.83-1.35-5.48-3.9-6.89-6.83l1.95-1.66c.27-.28.35-.67.24-1.02-.37-1.11-.56-2.3-.56-3.53 0-.54-.45-.99-.99-.99H4.19C3.65 3 3 3.24 3 3.99 3 13.28 10.73 21 20.01 21c.71 0 .99-.63.99-1.18v-3.45c0-.54-.45-.99-.99-.99z" />
                  </svg>
                )}
              </button>
              <span style={styles.btnLabel}>{isConnecting ? 'Connecting' : 'Accept'}</span>
            </div>

            <div style={styles.btnCol}>
              <button
                type="button"
                style={{
                  ...styles.roundBtn,
                  ...(isMuted ? styles.muteBtnActive : styles.muteBtn),
                }}
                onPointerDown={(e) => { e.preventDefault(); e.stopPropagation(); toggleMute(); }}
                title={isMuted ? 'Unmute' : 'Mute'}
              >
                {isMuted ? (
                  <svg width="26" height="26" viewBox="0 0 24 24" fill="#1a1a1a" style={{ pointerEvents: 'none' }}>
                    <path d="M19 11h-1.7c0 .74-.16 1.43-.43 2.05l1.23 1.23c.56-.98.9-2.09.9-3.28zm-4.02.17c0-.06.02-.11.02-.17V5c0-1.66-1.34-3-3-3S9 3.34 9 5v.18L14.98 11.17zM4.27 3L3 4.27l6.01 6.01V11c0 1.66 1.33 3 2.99 3 .22 0 .44-.03.65-.08l1.66 1.66c-.71.33-1.5.52-2.31.52-2.76 0-5.3-2.1-5.3-5.1H5c0 3.41 2.72 6.23 6 6.72V21h2v-3.28c.91-.13 1.77-.45 2.54-.9L19.73 21 21 19.73 4.27 3z" />
                  </svg>
                ) : (
                  <svg width="26" height="26" viewBox="0 0 24 24" fill="#fff" style={{ pointerEvents: 'none' }}>
                    <path d="M12 14c1.66 0 2.99-1.34 2.99-3L15 5c0-1.66-1.34-3-3-3S9 3.34 9 5v6c0 1.66 1.34 3 3 3zm5.3-3c0 3-2.54 5.1-5.3 5.1S6.7 14 6.7 11H5c0 3.41 2.72 6.23 6 6.72V21h2v-3.28c3.28-.48 6-3.3 6-6.72h-1.7z" />
                  </svg>
                )}
              </button>
              <span style={styles.btnLabel}>{isMuted ? 'Unmute' : 'Mute'}</span>
            </div>
          </div>
        )}

        {isActive && (
          <div style={styles.activeCallRow}>
            <div style={styles.btnCol}>
              <button
                type="button"
                style={{
                  ...styles.roundBtn,
                  ...(isMuted ? styles.muteBtnActive : styles.muteBtn),
                }}
                onPointerDown={(e) => { e.preventDefault(); e.stopPropagation(); toggleMute(); }}
                title={isMuted ? 'Unmute' : 'Mute'}
              >
                {isMuted ? (
                  <svg width="26" height="26" viewBox="0 0 24 24" fill="#1a1a1a" style={{ pointerEvents: 'none' }}>
                    <path d="M19 11h-1.7c0 .74-.16 1.43-.43 2.05l1.23 1.23c.56-.98.9-2.09.9-3.28zm-4.02.17c0-.06.02-.11.02-.17V5c0-1.66-1.34-3-3-3S9 3.34 9 5v.18L14.98 11.17zM4.27 3L3 4.27l6.01 6.01V11c0 1.66 1.33 3 2.99 3 .22 0 .44-.03.65-.08l1.66 1.66c-.71.33-1.5.52-2.31.52-2.76 0-5.3-2.1-5.3-5.1H5c0 3.41 2.72 6.23 6 6.72V21h2v-3.28c.91-.13 1.77-.45 2.54-.9L19.73 21 21 19.73 4.27 3z" />
                  </svg>
                ) : (
                  <svg width="26" height="26" viewBox="0 0 24 24" fill="#fff" style={{ pointerEvents: 'none' }}>
                    <path d="M12 14c1.66 0 2.99-1.34 2.99-3L15 5c0-1.66-1.34-3-3-3S9 3.34 9 5v6c0 1.66 1.34 3 3 3zm5.3-3c0 3-2.54 5.1-5.3 5.1S6.7 14 6.7 11H5c0 3.41 2.72 6.23 6 6.72V21h2v-3.28c3.28-.48 6-3.3 6-6.72h-1.7z" />
                  </svg>
                )}
              </button>
              <span style={styles.btnLabel}>{isMuted ? 'Unmute' : 'Mute'}</span>
            </div>

            <div style={styles.btnCol}>
              <button
                type="button"
                style={styles.endCallBtn}
                onPointerDown={(e) => { e.preventDefault(); e.stopPropagation(); terminateCall(); }}
                title="End Call"
              >
                <svg width="32" height="32" viewBox="0 0 24 24" fill="#fff" style={{ pointerEvents: 'none', transform: 'rotate(135deg)' }}>
                  <path d="M20.01 15.38c-1.23 0-2.42-.2-3.53-.56-.35-.12-.74-.03-1.01.24l-1.57 1.97c-2.83-1.35-5.48-3.9-6.89-6.83l1.95-1.66c.27-.28.35-.67.24-1.02-.37-1.11-.56-2.3-.56-3.53 0-.54-.45-.99-.99-.99H4.19C3.65 3 3 3.24 3 3.99 3 13.28 10.73 21 20.01 21c.71 0 .99-.63.99-1.18v-3.45c0-.54-.45-.99-.99-.99z" />
                </svg>
              </button>
              <span style={styles.btnLabel}>End</span>
            </div>
          </div>
        )}

        {isCalling && (
          <div style={styles.endCallWrap}>
            <button
              type="button"
              style={styles.endCallBtn}
              onPointerDown={(e) => { e.preventDefault(); e.stopPropagation(); terminateCall(); }}
              title="End Call"
            >
              <svg width="32" height="32" viewBox="0 0 24 24" fill="#fff" style={{ pointerEvents: 'none', transform: 'rotate(135deg)' }}>
                <path d="M20.01 15.38c-1.23 0-2.42-.2-3.53-.56-.35-.12-.74-.03-1.01.24l-1.57 1.97c-2.83-1.35-5.48-3.9-6.89-6.83l1.95-1.66c.27-.28.35-.67.24-1.02-.37-1.11-.56-2.3-.56-3.53 0-.54-.45-.99-.99-.99H4.19C3.65 3 3 3.24 3 3.99 3 13.28 10.73 21 20.01 21c.71 0 .99-.63.99-1.18v-3.45c0-.54-.45-.99-.99-.99z" />
              </svg>
            </button>
          </div>
        )}
      </div>
    </>
  );
}

export default PBXOutboundCallScreen;